import { inject, Injectable } from '@angular/core';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Subject, Subscription, timer } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly idle = inject(Idle);

  private readonly SESSION_DURATION_MINUTES = 5;
  private readonly DEFAULT_TIMEOUT = 60; // 1 minute warning modal countdown
  private readonly DEFAULT_IDLE =
    this.SESSION_DURATION_MINUTES * 60 - this.DEFAULT_TIMEOUT; // 14 minutes

  // SEPARATED REACTION CHANNELS
  public readonly requestBackendTokenRefresh$: Subject<void> =
    new Subject<void>();
  public readonly sessionTimedOut$: Subject<void> = new Subject<void>();
  public readonly sessionWarning$: Subject<number> = new Subject<number>();

  private idleStartSubscription?: Subscription;
  private idleEndSubscription?: Subscription;
  private tokenRefreshSubscription?: Subscription;
  private visualCountdownSubscription?: Subscription;

  constructor() {
    this.setupIdleEventListeners();
  }

  /**
   * TRACK 1 & 2 INITIALIZATION: Triggered once on initial Login or F5 page reloads.
   * Spawns both tracks into their independent parallel lifecycles.
   */
  public startTracking(tokenExpirationIso: string): void {
    this.stopTracking();

    // Initialize physical UI track boundary state (now + 15 min)
    this.updateUiExpiration(this.SESSION_DURATION_MINUTES * 60);

    // Boot up ngx-idle strictly for user physical activity monitoring
    this.idle.setIdle(this.DEFAULT_IDLE);
    this.idle.setTimeout(this.DEFAULT_TIMEOUT);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.watch();

    // Boot up the technical background auto-refresh track loop
    this.scheduleNextBackgroundRefresh(tokenExpirationIso);

    console.log('[Session] Dual tracking systems initialized successfully.');
  }

  /**
   * TRACK 1 - UI RESCUE: Called exclusively when the user interacts or clicks the SweetAlert2 button.
   * It restores the 15-minute interface timer in isolation. The backend track is untouched.
   */
  public rescueUiSession(): void {
    this.visualCountdownSubscription?.unsubscribe();

    this.updateUiExpiration(this.SESSION_DURATION_MINUTES * 60);
    this.idle.watch();

    console.log(
      '[Session] UI Track rescued explicitly by user choice. Interface reset to 15m.',
    );
  }

  /**
   * TRACK 2 - AUTONOMOUS BACKEND REFRESH LOOP
   * Reads the current expiration token date from storage at every cycle execution step
   * to precisely coordinate and schedule the next network refresh dispatch interval.
   */
  public scheduleNextBackgroundRefresh(tokenExpirationIso: string): void {
    this.tokenRefreshSubscription?.unsubscribe();

    const expirationTime = new Date(tokenExpirationIso).getTime();
    const currentTime = Date.now();
    const bufferSeconds = 30; // Safety margin: fetch token 30s before actual backend death

    const secondsUntilRefresh =
      Math.floor((expirationTime - currentTime) / 1000) - bufferSeconds;

    if (secondsUntilRefresh > 0) {
      console.log(
        `[Background Track] Scheduling next silent token refresh in ${secondsUntilRefresh} seconds.`,
      );

      this.tokenRefreshSubscription = timer(
        secondsUntilRefresh * 1000,
      ).subscribe(() => {
        console.log(
          '[Background Track] Lifecycle threshold reached. Dispatched refresh trigger to orchestrator.',
        );
        this.requestBackendTokenRefresh$.next();
      });
    } else {
      // If time math is already zero or negative, we trigger an immediate proactive sync attempt
      console.log(
        '[Background Track] Token time threshold low or expired. Dispatching instant sync.',
      );
      this.requestBackendTokenRefresh$.next();
    }
  }

  /**
   * Hard stop utility for local credential cleanups, manual logouts, or absolute evictions.
   */
  public stopTracking(): void {
    this.idle.stop();
    this.tokenRefreshSubscription?.unsubscribe();
    this.visualCountdownSubscription?.unsubscribe();
    console.log('[Session] All tracking engines stopped.');
  }

  public destroyService(): void {
    this.stopTracking();
    this.unsubscribeEventListeners();
  }

  /**
   * TRACK 1 - Bulletproof Visual Countdown Manager
   * Calculates remaining time based on a fixed future timestamp milestone.
   * This guarantees precision even if the browser freezes or throttles the tab in the background.
   */
  private startVisualCountdown(): void {
    this.visualCountdownSubscription?.unsubscribe();

    // 1. Calculate the exact absolute point in time when the eviction must happen
    const targetEvictionTime = Date.now() + this.DEFAULT_TIMEOUT * 1000; // Now + 60 seconds

    // Initial tick calculation to open the SweetAlert modal immediately
    this.sessionWarning$.next(this.DEFAULT_TIMEOUT);

    // 2. Start the tracking loop stream to evaluate the delta every second
    this.visualCountdownSubscription = timer(1000, 1000).subscribe(() => {
      const currentTime = Date.now();

      // 3. Mathematical delta: How many seconds are ACTUALLY left right now?
      const secondsLeft = Math.ceil((targetEvictionTime - currentTime) / 1000);

      if (secondsLeft > 0) {
        // Stream the exact calibrated time left to the AppComponent
        this.sessionWarning$.next(secondsLeft);
      } else {
        // If the delta is 0 or negative (the time passed while the tab was asleep), force immediate eviction
        this.visualCountdownSubscription?.unsubscribe();
        console.log(
          '[Session] Visual delta countdown hit zero or elapsed. Executing eviction.',
        );
        this.sessionTimedOut$.next();
      }
    });
  }

  private updateUiExpiration(durationInSeconds: number): void {
    const uiExpirationTime = Date.now() + durationInSeconds * 1000;
    sessionStorage.setItem('ui_exp', new Date(uiExpirationTime).toISOString());
  }

  /**
   * Event listeners strictly managing UI behavior and interactions.
   */
  private setupIdleEventListeners(): void {
    // Idle trigger boundaries hit (User was physically inactive for 14 minutes)
    this.idleStartSubscription = this.idle.onIdleStart.subscribe(() => {
      console.log(
        '[Session] UI Track: 14m inactivity boundary hit. Initializing visual ticker.',
      );
      this.updateUiExpiration(this.DEFAULT_TIMEOUT);
      this.startVisualCountdown();
    });

    // Handle standard micro interactions before entering strict idling countdown frames
    this.idleEndSubscription = this.idle.onIdleEnd.subscribe(() => {
      // Secure shield checkpoint: if the custom countdown ticker is active, ignore passive hardware noise
      if (this.visualCountdownSubscription) {
        console.log(
          '[Session] UI Track: Interaction blocked. Modal button click required.',
        );
        return;
      }

      console.log(
        '[Session] UI Track: Active interaction caught. Advancing 15m ui_exp baseline.',
      );
      this.updateUiExpiration(this.SESSION_DURATION_MINUTES * 60);
    });
  }

  private unsubscribeEventListeners(): void {
    this.idleStartSubscription?.unsubscribe();
    this.idleEndSubscription?.unsubscribe();
  }
}
